import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { useD3LineChart } from "./useD3LineChart";
import type { PerformanceChartVisualStyle } from "../components/performanceChartAppearance";
import { basketsService } from "../services/basketsService";
import { basketsV3Service } from "../services/basketsV3Service";
import { performanceMetricsService } from "../services/performanceMetricsService";
import { contractsV2Service } from "../services/contractsV2Service";
import {
  FactorEntry,
  FactorsChartMetric,
  FactorsChartEntityType,
  factorsToPerformanceMetrics,
  productFactorsService,
} from "../services/productFactorsService";
import { discoverFactorsService } from "../services/discoverFactorsService";
import { shareableLinksService } from "../services/shareableLinksService";
import investorStrategyService from "../services/investorStrategyService";
import managedStrategyService from "../services/managedStrategyService";
import { portfoliosService } from "../services/portfoliosService";
import { portfoliosV3Service } from "../services/portfoliosV3Service";
import { formatPercentage, formatSignedPercentage } from "../utils/numberUtils";
import {
  bucketFactorsToCumulativeSeries,
  clipFactorsFromLaunch,
  computeCumulativePerformance,
  getV3FilterWindowBounds,
  getBucketMsForFilter,
  resolveLaunchClipMs,
  V3_FACTOR_SLOT_MS,
  V3_INVESTOR_TIME_FILTER_OPTIONS,
  V3_TIME_FILTER_OPTIONS,
  V3TimeFilter,
} from "../components/performanceChartV3Utils";

const LEGACY_TIME_FILTER_OPTIONS = [
  { value: "1 week", label: "1W" },
  { value: "1 month", label: "1M" },
  { value: "3 months", label: "3M" },
  { value: "6 months", label: "6M" },
  { value: "Year to date", label: "YTD" },
  { value: "1 year", label: "1Y" },
  { value: "All", label: "All" },
  { value: "Since inception", label: "Inception" },
];

const V3_FACTORS_CACHE_TTL_MS = 5 * 60 * 1000;

interface V3FactorsCacheEntry {
  createdAt: string | null;
  factors: FactorEntry[];
  nextPage: number;
  totalPages: number | null;
  isComplete: boolean;
  lastUpdatedAtMs: number | null;
  inFlight: Promise<void> | null;
}

const V3_FACTORS_CACHE = new Map<string, V3FactorsCacheEntry>();

function mergeFactorsAscending(existing: FactorEntry[], incoming: FactorEntry[]): FactorEntry[] {
  if (incoming.length === 0) return existing;

  const merged = new Map<string, number>();
  existing.forEach((factor) => merged.set(factor.t, factor.f));
  incoming.forEach((factor) => merged.set(factor.t, factor.f));

  return [...merged.entries()]
    .map(([t, f]) => ({ t, f }))
    .sort((a, b) => new Date(a.t).getTime() - new Date(b.t).getTime());
}

export interface UsePerformanceChartDataProps {
  isActive: boolean;
  entityId: number;
  entityType: FactorsChartEntityType;
  entityName: string;
  providerId?: number | string;
  forceLightMode?: boolean;
  seriesColor?: string;
  visualStyle?: PerformanceChartVisualStyle;
  dataSource?:
    | "legacy-metrics"
    | "v3-factors"
    | "v3-factors-public"
    | "v3-factors-admin"
    | "v3-managed-strategy-factors-admin";
  entityCreatedAt?: string | null;
  computedAtUtc?: string | null;
  height?: number;
  margins?: { top: number; right: number; bottom: number; left: number };
  adminUserId?: string;
  showTodayFilter?: boolean;
  /**
   * The shown user's `launch_date`. Only honored for `entityType === "STRATEGY"`
   * (the customer's computed performance); when supplied and not in the future,
   * pre-launch factors are dropped so the standalone strategy chart and its
   * period-return table start at launch. Ignored for other entity types.
   */
  launchDate?: string | null;
}

export interface PerformanceChartDataResult {
  loading: boolean;
  error: string | null;
  /** Launch-clipped source factors used to derive the V3 chart and returns. */
  factors: FactorEntry[];
  /** Retries the current source without requiring the consumer to remount. */
  retry: () => void;
  performanceMetrics: FactorsChartMetric[];
  svgRef: React.RefObject<SVGSVGElement | null>;
  containerRef: React.RefObject<HTMLDivElement | null>;
  tooltipRef: React.RefObject<HTMLDivElement | null>;
  chartHeight: number;
  chartData: any[];
  timeFilterOptions: ReadonlyArray<{ readonly value: string; readonly label: string }>;
  availableFilters: string[];
  selectedPeriod: string;
  handleSelectPeriod: (value: string) => void;
  formattedCreateDate: string | null;
  formatPerformance: (value: number) => string;
  formatLocalDate: (dateString: string) => string;
  orderedFiltersForTable: string[];
  timeframePerformance: Record<string, number | null>;
  v3IntrinsicAvailability: Record<V3TimeFilter, boolean>;
  v3LoadedCoverage: Record<V3TimeFilter, boolean>;
  v3HasDataByPeriod: Record<V3TimeFilter, boolean>;
  isDarkMode: boolean;
  isV3Factors: boolean;
  entityType: FactorsChartEntityType;
  entityName: string;
}

export function usePerformanceChartData({
  isActive,
  entityId,
  entityType,
  entityName,
  providerId: propProviderId,
  forceLightMode = false,
  seriesColor,
  visualStyle,
  dataSource = "legacy-metrics",
  entityCreatedAt: propEntityCreatedAt,
  computedAtUtc: propComputedAtUtc,
  height,
  margins,
  adminUserId,
  showTodayFilter = false,
  launchDate,
}: UsePerformanceChartDataProps): PerformanceChartDataResult {
  const { user, effectiveUserId } = useAuth();
  const { isDarkMode: themeDarkMode } = useTheme();
  const isDarkMode = forceLightMode ? false : themeDarkMode;
  const isV3Factors =
    dataSource === "v3-factors" ||
    dataSource === "v3-factors-public" ||
    dataSource === "v3-factors-admin" ||
    dataSource === "v3-managed-strategy-factors-admin";
  // Launch-date anchor for the customer's computed performance. Only the
  // STRATEGY entity carries a launch_date; other entity types never clip even
  // if a launchDate is passed. Future/invalid dates resolve to null (no clip).
  const launchClipMs = useMemo(
    () => (entityType === "STRATEGY" ? resolveLaunchClipMs(launchDate) : null),
    [entityType, launchDate]
  );
  const v3TimeFilterOptions = useMemo(
    () => (showTodayFilter ? V3_INVESTOR_TIME_FILTER_OPTIONS : V3_TIME_FILTER_OPTIONS),
    [showTodayFilter]
  );
  const v3FixedFilters = useMemo(
    () => v3TimeFilterOptions.map((option) => option.value) as V3TimeFilter[],
    [v3TimeFilterOptions]
  );
  const effectiveDarkMode = isV3Factors ? !forceLightMode : isDarkMode;
  const [performanceMetrics, setPerformanceMetrics] = useState<FactorsChartMetric[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<string>("1 year");
  const [userOverrodePeriod, setUserOverrodePeriod] = useState<boolean>(false);
  const [entityCreateDate, setEntityCreateDate] = useState<string | null>(null);
  const [v3Factors, setV3Factors] = useState<FactorEntry[]>([]);
  const [v3IsComplete, setV3IsComplete] = useState(false);
  const entityCreateDateRef = useRef<string | null>(null);
  const isActiveRef = useRef<boolean>(false);
  const v3SessionIdRef = useRef(0);
  const accountOwnerUserId = effectiveUserId === undefined
    ? user?.id ?? null
    : effectiveUserId;
  const v3CachePrincipal = dataSource === "v3-factors"
    ? accountOwnerUserId === null
      ? `effective-unresolved:${user?.id ?? "anon"}`
      : `effective:${accountOwnerUserId}`
    : `actor:${user?.id ?? "anon"}`;
  const v3CacheKey = useMemo(
    () =>
      `${v3CachePrincipal}:${dataSource}:${propProviderId ?? ""}:${entityType}:${entityId}:${adminUserId ?? ""}:${propComputedAtUtc ?? ""}`,
    [adminUserId, dataSource, entityId, entityType, propComputedAtUtc, propProviderId, v3CachePrincipal]
  );

  const beginV3Session = useCallback(() => {
    v3SessionIdRef.current += 1;
    return v3SessionIdRef.current;
  }, []);

  const invalidateV3Session = useCallback(() => {
    v3SessionIdRef.current += 1;
  }, []);

  const isCurrentV3Session = useCallback(
    (sessionId: number) => sessionId === v3SessionIdRef.current && isActiveRef.current,
    []
  );

  const updateEntityCreateDate = useCallback((value: string | null) => {
    entityCreateDateRef.current = value;
    setEntityCreateDate(value);
  }, []);

  useEffect(() => {
    isActiveRef.current = isActive;
    return () => {
      isActiveRef.current = false;
    };
  }, [isActive]);

  const loadLegacyEntityDetailsWithMetrics = useCallback(
    async (metrics: FactorsChartMetric[]) => {
      try {
        let providerId: number | string | null = propProviderId || null;

        if (!providerId && metrics.length > 0 && metrics[0].providerId) {
          providerId = metrics[0].providerId;
        }

        if (entityType === "PORTFOLIO") {
          if (!providerId) {
            console.error("No providerId available, cannot fetch portfolio");
            return;
          }
          const portfolio = await portfoliosService.getProviderPortfolioById(
            providerId.toString(),
            entityId.toString()
          );
          updateEntityCreateDate(portfolio.createdAt);
        } else if (entityType === "BASKET") {
          if (!providerId) {
            console.error("No providerId available, cannot fetch basket");
            return;
          }

          const baskets = await basketsService.getProviderBaskets(providerId.toString());
          const basket = baskets.find(b => b.id === entityId.toString() || Number(b.id) === entityId);

          if (basket) {
            updateEntityCreateDate(basket.createdAt);
          }
        }
      } catch (err) {
        console.error("Failed to load entity details:", err);
      }
    },
    [entityId, entityType, propProviderId, updateEntityCreateDate]
  );

  const loadV3EntityDetails = useCallback(async (sessionId: number): Promise<string | null> => {
    try {
      if (propEntityCreatedAt) {
        updateEntityCreateDate(propEntityCreatedAt);
        return propEntityCreatedAt;
      }

      if (
        entityType === "CONTRACT" ||
        entityType === "STRATEGY" ||
        entityType === "MANAGED_STRATEGY"
      ) {
        updateEntityCreateDate(null);
        return null;
      }

      if (entityType === "PORTFOLIO") {
        const portfolio = await portfoliosV3Service.getPortfolio(entityId);
        if (!isCurrentV3Session(sessionId)) return null;
        const createdAt = portfolio.portfolio.createdAtUtc;
        updateEntityCreateDate(createdAt);
        return createdAt;
      }

      const basket = await basketsV3Service.getBasket(entityId);
      if (!isCurrentV3Session(sessionId)) return null;
      const createdAt = basket.basket.createdAtUtc;
      updateEntityCreateDate(createdAt);
      return createdAt;
    } catch (err) {
      console.error("Failed to load V3 entity details:", err);
      return null;
    }
  }, [entityId, entityType, isCurrentV3Session, propEntityCreatedAt, updateEntityCreateDate]);

  const getOrInitV3CacheEntry = useCallback((): V3FactorsCacheEntry => {
    const existing = V3_FACTORS_CACHE.get(v3CacheKey);
    if (existing) return existing;

    const created: V3FactorsCacheEntry = {
      createdAt: null,
      factors: [],
      nextPage: 1,
      totalPages: null,
      isComplete: false,
      lastUpdatedAtMs: null,
      inFlight: null,
    };
    V3_FACTORS_CACHE.set(v3CacheKey, created);
    return created;
  }, [v3CacheKey]);

  const applyV3FactorsSnapshot = useCallback(
    (cacheEntry: V3FactorsCacheEntry) => {
      // Clip at launch (STRATEGY self/admin view) before deriving metrics, so
      // every window -- including trailing ones that ignore entityCreateDate --
      // excludes pre-launch history. The cache keeps raw factors; clipping is a
      // display-only transform here.
      const clipped = clipFactorsFromLaunch(cacheEntry.factors, launchClipMs);
      setV3Factors(clipped);
      setPerformanceMetrics(factorsToPerformanceMetrics(clipped, entityId, entityType));
      setV3IsComplete(cacheEntry.isComplete);
    },
    [entityId, entityType, launchClipMs]
  );

  const fetchNextV3Page = useCallback(async (sessionId: number): Promise<boolean> => {
    if (!isCurrentV3Session(sessionId)) return false;
    const cacheEntry = getOrInitV3CacheEntry();

    if (cacheEntry.inFlight) {
      await cacheEntry.inFlight;
      if (!isCurrentV3Session(sessionId)) return false;
      applyV3FactorsSnapshot(cacheEntry);
      return !cacheEntry.isComplete;
    }

    if (cacheEntry.isComplete) {
      return false;
    }

    const pageToFetch = cacheEntry.nextPage;
    const factorsTo = propComputedAtUtc ?? undefined;
    cacheEntry.inFlight = (async () => {
      if (
        entityType === "MANAGED_STRATEGY" &&
        dataSource !== "v3-managed-strategy-factors-admin"
      ) {
        throw new Error("Managed strategy charts require the admin managed-strategy factor source");
      }
      const response = entityType === "CONTRACT"
        ? await contractsV2Service.getContractFactorsPage(entityId, pageToFetch)
        : entityType === "STRATEGY"
        ? adminUserId
          ? await investorStrategyService.getStrategyFactorsPageForUser(adminUserId, { page: pageToFetch })
          : await investorStrategyService.getStrategyFactorsPage({ page: pageToFetch })
        : entityType === "MANAGED_STRATEGY"
          ? await managedStrategyService.getFactorsPage(entityId, {
              page: pageToFetch,
              pageSize: 10000,
              to: factorsTo,
            })
        : dataSource === "v3-factors-admin" && propProviderId
          ? await discoverFactorsService.getAdminFactorsPage(
              Number(propProviderId),
              entityType === "BASKET" ? "basket" : "portfolio",
              entityId,
              pageToFetch,
              undefined,
              factorsTo
            )
        : dataSource === "v3-factors-public" && propProviderId
          ? await shareableLinksService.getPublicFactorsPage(
              Number(propProviderId),
              entityType === "BASKET" ? "basket" : "portfolio",
              entityId,
              pageToFetch,
              undefined,
              factorsTo
            )
          : propProviderId
            ? await discoverFactorsService.getFactorsPage(
                Number(propProviderId),
                entityType === "BASKET" ? "basket" : "portfolio",
                entityId,
                pageToFetch,
                undefined,
                factorsTo
              )
            : await productFactorsService.getFactorsPage(
                entityType === "BASKET" ? "basket" : "portfolio",
                entityId,
                pageToFetch,
                undefined,
                factorsTo
              );
      cacheEntry.totalPages = response.totalPages;
      cacheEntry.factors = mergeFactorsAscending(cacheEntry.factors, response.items);
      cacheEntry.nextPage = pageToFetch + 1;
      cacheEntry.lastUpdatedAtMs = Date.now();
      if (
        response.currentPageNumOfItems === 0 ||
        cacheEntry.nextPage > response.totalPages
      ) {
        cacheEntry.isComplete = true;
      }
    })();

    try {
      await cacheEntry.inFlight;
      if (!isCurrentV3Session(sessionId)) return false;
      applyV3FactorsSnapshot(cacheEntry);
      return !cacheEntry.isComplete;
    } finally {
      cacheEntry.inFlight = null;
    }
  }, [adminUserId, applyV3FactorsSnapshot, dataSource, entityId, entityType, getOrInitV3CacheEntry, isCurrentV3Session, propComputedAtUtc, propProviderId]);

  const ensureV3CoverageForPeriod = useCallback(
    async (period: V3TimeFilter, sessionId: number) => {
      if (!isCurrentV3Session(sessionId)) return;
      const cacheEntry = getOrInitV3CacheEntry();

      if (cacheEntry.factors.length === 0 && !cacheEntry.isComplete) {
        await fetchNextV3Page(sessionId);
        if (!isCurrentV3Session(sessionId)) return;
      }

      if (period === "All" || period === "Since inception") {
        while (isCurrentV3Session(sessionId)) {
          const latestEntry = getOrInitV3CacheEntry();
          if (latestEntry.isComplete) break;
          const progressed = await fetchNextV3Page(sessionId);
          if (!progressed) break;
        }
        return;
      }

      const latestLoaded = cacheEntry.factors[cacheEntry.factors.length - 1];
      if (!latestLoaded) {
        return;
      }

      const anchor = new Date(latestLoaded.t);
      const { start: requiredStart } = getV3FilterWindowBounds(
        period,
        anchor,
        entityCreateDateRef.current
      );
      if (!requiredStart) {
        return;
      }

      while (isCurrentV3Session(sessionId)) {
        const latestEntry = getOrInitV3CacheEntry();
        const oldestLoaded = latestEntry.factors[0];
        if (!oldestLoaded) {
          if (latestEntry.isComplete) break;
          const progressed = await fetchNextV3Page(sessionId);
          if (!progressed) break;
          continue;
        }

        const earliestCoveredStartMs = new Date(oldestLoaded.t).getTime() - V3_FACTOR_SLOT_MS;
        if (earliestCoveredStartMs <= requiredStart.getTime()) {
          break;
        }
        if (latestEntry.isComplete) {
          break;
        }

        const progressed = await fetchNextV3Page(sessionId);
        if (!progressed) {
          break;
        }
      }
    },
    [fetchNextV3Page, getOrInitV3CacheEntry, isCurrentV3Session]
  );

  const prefetchRemainingV3Pages = useCallback(async (sessionId: number) => {
    while (isCurrentV3Session(sessionId)) {
      const cacheEntry = getOrInitV3CacheEntry();
      if (cacheEntry.isComplete) break;
      const progressed = await fetchNextV3Page(sessionId);
      if (!progressed) break;
      await new Promise((resolve) => setTimeout(resolve, 40));
    }
  }, [fetchNextV3Page, getOrInitV3CacheEntry, isCurrentV3Session]);

  const loadLegacyPerformanceMetrics = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      if (
        entityType === "CONTRACT" ||
        entityType === "STRATEGY" ||
        entityType === "MANAGED_STRATEGY"
      ) {
        console.error(`Legacy performance metrics are not supported for ${entityType} entities`);
        setError("Failed to load performance data.");
        return;
      }

      const metrics = await performanceMetricsService.getMetricsByEntity(entityId, entityType, false);
      setPerformanceMetrics(metrics);
      await loadLegacyEntityDetailsWithMetrics(metrics);
    } catch (err) {
      console.error("Failed to load performance metrics:", err);
      setError("Failed to load performance data.");
    } finally {
      setLoading(false);
    }
  }, [entityId, entityType, loadLegacyEntityDetailsWithMetrics]);

  const initializeV3Performance = useCallback(async (sessionId: number) => {
    try {
      if (!isCurrentV3Session(sessionId)) return;
      setLoading(true);
      setError(null);
      let cacheEntry = getOrInitV3CacheEntry();
      const shouldExpireCache =
        cacheEntry.isComplete &&
        cacheEntry.lastUpdatedAtMs !== null &&
        Date.now() - cacheEntry.lastUpdatedAtMs > V3_FACTORS_CACHE_TTL_MS;
      const shouldRefreshFromFirstPage = !cacheEntry.isComplete && cacheEntry.factors.length > 0;
      if (shouldExpireCache || shouldRefreshFromFirstPage) {
        V3_FACTORS_CACHE.delete(v3CacheKey);
        cacheEntry = getOrInitV3CacheEntry();
      }
      if (!isCurrentV3Session(sessionId)) return;
      if (cacheEntry.createdAt) {
        updateEntityCreateDate(cacheEntry.createdAt);
      }
      if (cacheEntry.factors.length > 0) {
        applyV3FactorsSnapshot(cacheEntry);
      } else {
        setV3Factors([]);
        setV3IsComplete(cacheEntry.isComplete);
      }

      const createdAt =
        cacheEntry.createdAt ??
        (await loadV3EntityDetails(sessionId));
      if (!isCurrentV3Session(sessionId)) return;
      cacheEntry.createdAt = createdAt;

      const defaultFilter: V3TimeFilter = "Since inception";
      if (!isCurrentV3Session(sessionId)) return;
      setSelectedPeriod(defaultFilter);
      await ensureV3CoverageForPeriod(defaultFilter, sessionId);
      if (!isCurrentV3Session(sessionId)) return;
      applyV3FactorsSnapshot(cacheEntry);
      void prefetchRemainingV3Pages(sessionId).catch((err) => {
        console.error("Failed to prefetch remaining V3 pages:", err);
      });
    } catch (err) {
      if (!isCurrentV3Session(sessionId)) return;
      console.error("Failed to initialize V3 performance chart:", err);
      setError("Failed to load performance data.");
    } finally {
      if (!isCurrentV3Session(sessionId)) return;
      setLoading(false);
    }
  }, [
    applyV3FactorsSnapshot,
    ensureV3CoverageForPeriod,
    getOrInitV3CacheEntry,
    isCurrentV3Session,
    loadV3EntityDetails,
    prefetchRemainingV3Pages,
    updateEntityCreateDate,
    v3CacheKey,
  ]);

  useEffect(() => {
    if (!isActive) {
      invalidateV3Session();
      return;
    }

    setUserOverrodePeriod(false);
    setError(null);
    setV3IsComplete(false);
    updateEntityCreateDate(null);
    setPerformanceMetrics([]);
    if (isV3Factors) {
      setSelectedPeriod("Since inception");
      const sessionId = beginV3Session();
      void initializeV3Performance(sessionId);
      return;
    }

    invalidateV3Session();
    setSelectedPeriod("1 year");
    void loadLegacyPerformanceMetrics();
  }, [
    beginV3Session,
    initializeV3Performance,
    invalidateV3Session,
    isActive,
    isV3Factors,
    loadLegacyPerformanceMetrics,
    updateEntityCreateDate,
  ]);

  const hasDataForFilter = useCallback(
    (filter: string, metrics: FactorsChartMetric[]) => {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      switch (filter) {
        case "All": {
          return metrics.length > 0;
        }
        case "1 week": {
          const oneWeekAgo = new Date(now);
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
          oneWeekAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneWeekAgo;
          });
        }
        case "Since inception": {
          if (!entityCreateDate) return metrics.length > 0;
          const inceptionDate = new Date(entityCreateDate);
          inceptionDate.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= inceptionDate;
          });
        }
        case "Year to date": {
          const yearStart = new Date(now.getFullYear(), 0, 1);
          yearStart.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= yearStart;
          });
        }
        case "Month to date": {
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          monthStart.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= monthStart;
          });
        }
        case "1 month": {
          const oneMonthAgo = new Date(now);
          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
          oneMonthAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneMonthAgo;
          });
        }
        case "6 months": {
          const sixMonthsAgo = new Date(now);
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          sixMonthsAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= sixMonthsAgo;
          });
        }
        case "3 months": {
          const threeMonthsAgo = new Date(now);
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          threeMonthsAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= threeMonthsAgo;
          });
        }
        case "1 year": {
          const oneYearAgo = new Date(now);
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          oneYearAgo.setHours(0, 0, 0, 0);
          return metrics.some(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneYearAgo;
          });
        }
        default:
          return metrics.length > 0;
      }
    },
    [entityCreateDate]
  );

  const v3IntrinsicAvailability = useMemo(() => {
    return v3FixedFilters.reduce<Record<V3TimeFilter, boolean>>((acc, period) => {
      acc[period] = v3Factors.length > 0;
      return acc;
    }, {} as Record<V3TimeFilter, boolean>);
  }, [v3Factors, v3FixedFilters]);

  const v3LoadedCoverage = useMemo(() => {
    const latestFactor = v3Factors.length > 0 ? v3Factors[v3Factors.length - 1] : null;
    const anchor = latestFactor ? new Date(latestFactor.t) : null;
    const oldestLoaded = v3Factors[0];
    const oldestLoadedMs = oldestLoaded ? new Date(oldestLoaded.t).getTime() : Number.POSITIVE_INFINITY;

    return v3FixedFilters.reduce<Record<V3TimeFilter, boolean>>((acc, period) => {
      if (period === "All" || period === "Since inception") {
        acc[period] = v3Factors.length > 0 && v3IsComplete;
        return acc;
      }

      if (!anchor) {
        acc[period] = false;
        return acc;
      }

      const { start: requiredStart } = getV3FilterWindowBounds(period, anchor, entityCreateDate);
      if (!requiredStart) {
        acc[period] = v3Factors.length > 0;
        return acc;
      }

      const earliestCoveredStartMs = oldestLoadedMs - V3_FACTOR_SLOT_MS;
      acc[period] = earliestCoveredStartMs <= requiredStart.getTime();
      return acc;
    }, {} as Record<V3TimeFilter, boolean>);
  }, [entityCreateDate, v3Factors, v3FixedFilters, v3IsComplete]);

  const filterV3MetricsByPeriod = useCallback(
    (metrics: FactorsChartMetric[], period: V3TimeFilter): FactorsChartMetric[] => {
      if (!v3LoadedCoverage[period]) return [];

      if (period === "All") {
        return metrics;
      }

      const latestMetric = metrics.length > 0 ? metrics[metrics.length - 1] : null;
      if (!latestMetric) return [];

      const anchor = new Date(latestMetric.metricDate);
      const { start, end } = getV3FilterWindowBounds(period, anchor, entityCreateDateRef.current);
      const endMs = end.getTime();
      if (!start) {
        return metrics.filter((metric) => new Date(metric.metricDate).getTime() <= endMs);
      }

      const startMs = start.getTime();
      return metrics.filter((metric) => {
        const metricMs = new Date(metric.metricDate).getTime();
        return metricMs > startMs && metricMs <= endMs;
      });
    },
    [v3LoadedCoverage]
  );

  const v3HasDataByPeriod = useMemo(() => {
    const factorMetrics = performanceMetrics.filter((metric) => metric.metricName === "factor");
    return v3FixedFilters.reduce<Record<V3TimeFilter, boolean>>((acc, period) => {
      acc[period] = filterV3MetricsByPeriod(factorMetrics, period).length > 0;
      return acc;
    }, {} as Record<V3TimeFilter, boolean>);
  }, [filterV3MetricsByPeriod, performanceMetrics, v3FixedFilters]);

  const availableFilters = useMemo(() => {
    if (isV3Factors) {
      if (entityType === "STRATEGY") return v3FixedFilters.filter(f => f !== "All");
      return [...v3FixedFilters];
    }

    const factorMetrics = performanceMetrics.filter(m => m.metricName === "factor");
    if (factorMetrics.length === 0) return ["Since inception"];

    const allFilters = [
      "1 week",
      "Month to date",
      "1 month",
      "3 months",
      "6 months",
      "Year to date",
      "1 year",
      "Since inception",
      "All",
    ];

    return allFilters.filter(filter => hasDataForFilter(filter, factorMetrics));
  }, [entityType, hasDataForFilter, isV3Factors, performanceMetrics, v3FixedFilters]);

  const orderedFiltersForTable = useMemo(() => {
    if (isV3Factors) {
      if (entityType === "STRATEGY") return v3FixedFilters.filter(f => f !== "All");
      return [...v3FixedFilters];
    }

    const specials = ["All", "Since inception"];
    const presentSpecials = specials.filter(f => (availableFilters as string[]).includes(f));
    const rest = (availableFilters as string[]).filter(f => !specials.includes(f));
    return [...presentSpecials, ...rest];
  }, [availableFilters, entityType, isV3Factors, v3FixedFilters]);

  useEffect(() => {
    const filters = availableFilters as string[];
    if (!filters || filters.length === 0) return;

    if (isV3Factors) {
      if (!v3FixedFilters.includes(selectedPeriod as V3TimeFilter)) {
        setSelectedPeriod("Since inception");
        return;
      }

      const selectedV3 = selectedPeriod as V3TimeFilter;
      if (!userOverrodePeriod && selectedV3 !== "Since inception") {
        setSelectedPeriod("Since inception");
      }
      return;
    }

    if (!userOverrodePeriod && filters.includes("1 year") && selectedPeriod !== "1 year") {
      setSelectedPeriod("1 year");
      return;
    }
    if (!filters.includes(selectedPeriod)) {
      const preferred = [
        "1 year",
        "Year to date",
        "6 months",
        "3 months",
        "1 month",
        "1 week",
        "Since inception",
        "All",
      ];
      const next = preferred.find(f => filters.includes(f)) || filters[0];
      if (next) setSelectedPeriod(next);
    }
  }, [
    availableFilters,
    isV3Factors,
    selectedPeriod,
    userOverrodePeriod,
    v3FixedFilters,
  ]);

  const handleSelectPeriod = async (value: string) => {
    setUserOverrodePeriod(true);
    setSelectedPeriod(value);

    if (!isV3Factors) return;
    const sessionId = v3SessionIdRef.current;

    try {
      if (!isCurrentV3Session(sessionId)) return;
      setLoading(true);
      setError(null);
      const period = value as V3TimeFilter;
      await ensureV3CoverageForPeriod(period, sessionId);
      if (!isCurrentV3Session(sessionId)) return;
      const cacheEntry = getOrInitV3CacheEntry();
      applyV3FactorsSnapshot(cacheEntry);
      void prefetchRemainingV3Pages(sessionId).catch((err) => {
        console.error("Failed to prefetch remaining V3 pages:", err);
      });
    } catch (err) {
      if (!isCurrentV3Session(sessionId)) return;
      console.error("Failed to load V3 period data:", err);
      setError("Failed to load performance data.");
    } finally {
      if (!isCurrentV3Session(sessionId)) return;
      setLoading(false);
    }
  };

  const retry = useCallback(() => {
    if (!isActive) return;

    setUserOverrodePeriod(false);
    if (isV3Factors) {
      const sessionId = beginV3Session();
      void initializeV3Performance(sessionId);
      return;
    }

    invalidateV3Session();
    void loadLegacyPerformanceMetrics();
  }, [
    beginV3Session,
    initializeV3Performance,
    invalidateV3Session,
    isActive,
    isV3Factors,
    loadLegacyPerformanceMetrics,
  ]);

  const filterMetricsByPeriod = useCallback(
    (metrics: FactorsChartMetric[]) => {
      if (isV3Factors) {
        return filterV3MetricsByPeriod(metrics, selectedPeriod as V3TimeFilter);
      }

      const now = new Date();
      now.setHours(0, 0, 0, 0);

      switch (selectedPeriod) {
        case "All": {
          return metrics;
        }
        case "1 week": {
          const oneWeekAgo = new Date(now);
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
          oneWeekAgo.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneWeekAgo;
          });
        }
        case "Since inception": {
          if (!entityCreateDate) return metrics;
          const inceptionDate = new Date(entityCreateDate);
          inceptionDate.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= inceptionDate;
          });
        }
        case "Year to date": {
          const yearStart = new Date(now.getFullYear(), 0, 1);
          yearStart.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= yearStart;
          });
        }
        case "Month to date": {
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          monthStart.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= monthStart;
          });
        }
        case "1 month": {
          const oneMonthAgo = new Date(now);
          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
          oneMonthAgo.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneMonthAgo;
          });
        }
        case "6 months": {
          const sixMonthsAgo = new Date(now);
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          sixMonthsAgo.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= sixMonthsAgo;
          });
        }
        case "3 months": {
          const threeMonthsAgo = new Date(now);
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          threeMonthsAgo.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= threeMonthsAgo;
          });
        }
        case "1 year": {
          const oneYearAgo = new Date(now);
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          oneYearAgo.setHours(0, 0, 0, 0);
          return metrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneYearAgo;
          });
        }
        default:
          return metrics;
      }
    },
    [entityCreateDate, filterV3MetricsByPeriod, isV3Factors, selectedPeriod]
  );

  const getPerformanceChartData = useCallback(() => {
    if (performanceMetrics.length === 0) return [];

    const factorMetrics = performanceMetrics.filter(m => m.metricName === "factor");
    if (factorMetrics.length === 0) return [];
    const filtered = filterMetricsByPeriod(factorMetrics);
    if (filtered.length === 0) return [];
    const sorted = [...filtered].sort((a, b) => new Date(a.metricDate).getTime() - new Date(b.metricDate).getTime());

    if (isV3Factors) {
      const latestMetric = factorMetrics[factorMetrics.length - 1];
      if (!latestMetric) return [];

      const anchor = new Date(latestMetric.metricDate);
      const { start } = getV3FilterWindowBounds(
        selectedPeriod as V3TimeFilter,
        anchor,
        entityCreateDateRef.current
      );
      const fallbackStart = new Date(sorted[0].metricDate);
      const chartStart = start ?? fallbackStart;
      const chartStartMs = chartStart.getTime();
      const bucketMs = getBucketMsForFilter(selectedPeriod as V3TimeFilter);
      return bucketFactorsToCumulativeSeries(
        sorted.map((metric) => ({ t: metric.metricDate, f: metric.metricValue })),
        chartStartMs,
        bucketMs
      ) as any;
    }

    let cumulative = 1;
    const cumulativePoints = sorted.map(metric => {
      cumulative *= metric.metricValue;
      return {
        date: metric.metricDate.split("T")[0],
        performance: (cumulative - 1) * 100,
      };
    });

    if (sorted.length > 0) {
      const firstDateStr = sorted[0].metricDate.split("T")[0];
      const [year, month, day] = firstDateStr.split("-").map(Number);
      const baselineDate = new Date(Date.UTC(year, month - 1, day - 1));
      const baselineDateStr = baselineDate.toISOString().split("T")[0];
      return [{ date: baselineDateStr, performance: 0 }, ...cumulativePoints] as any;
    }
    return cumulativePoints as any;
  }, [filterMetricsByPeriod, isV3Factors, performanceMetrics, selectedPeriod]);

  const formatPerformanceFn = useCallback(
    (value: number) => isV3Factors ? formatSignedPercentage(value, 2) : formatSignedPercentage(value),
    [isV3Factors]
  );

  const formatLocalDateFn = useCallback((dateString: string) => {
    const datePart = dateString.includes("T") ? dateString.split("T")[0] : dateString;
    const [yyyy, mm, dd] = datePart.split("-").map(Number);
    const date = yyyy && mm && dd ? new Date(yyyy, mm - 1, dd) : new Date(dateString);
    return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  }, []);

  const getFormattedCreateDate = useCallback(() => {
    if (!entityCreateDate) return null;
    if (isV3Factors) {
      return entityCreateDate;
    }
    return entityCreateDate.split("T")[0];
  }, [entityCreateDate, isV3Factors]);

  const timeframePerformance = useMemo(() => {
    if (isV3Factors) {
      const factorMetrics = performanceMetrics.filter((metric) => metric.metricName === "factor");
      const metrics: Record<string, number | null> = {};

      v3FixedFilters.forEach((period) => {
        if (period !== "All" && period !== "Since inception" && !v3IntrinsicAvailability[period]) {
          metrics[period] = null;
          return;
        }
        if (!v3LoadedCoverage[period]) {
          metrics[period] = null;
          return;
        }

        const filtered = filterV3MetricsByPeriod(factorMetrics, period);
        metrics[period] = computeCumulativePerformance(filtered);
      });

      return metrics;
    }

    if (performanceMetrics.length === 0) return {};

    const factorMetrics = performanceMetrics.filter(m => m.metricName === "factor");
    if (factorMetrics.length === 0) return {};

    const metrics: Record<string, number | null> = {};
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    availableFilters.forEach(filter => {
      let filtered: FactorsChartMetric[] = [];

      switch (filter) {
        case "All":
          filtered = factorMetrics;
          break;
        case "1 week": {
          const oneWeekAgo = new Date(now);
          oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);
          oneWeekAgo.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneWeekAgo;
          });
          break;
        }
        case "Since inception":
          if (entityCreateDate) {
            const inceptionDate = new Date(entityCreateDate);
            inceptionDate.setHours(0, 0, 0, 0);
            filtered = factorMetrics.filter(m => {
              const metricDate = new Date(m.metricDate);
              metricDate.setHours(0, 0, 0, 0);
              return metricDate >= inceptionDate;
            });
          } else {
            filtered = factorMetrics;
          }
          break;
        case "Year to date": {
          const yearStart = new Date(now.getFullYear(), 0, 1);
          yearStart.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= yearStart;
          });
          break;
        }
        case "Month to date": {
          const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);
          monthStart.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= monthStart;
          });
          break;
        }
        case "1 month": {
          const oneMonthAgo = new Date(now);
          oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
          oneMonthAgo.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneMonthAgo;
          });
          break;
        }
        case "3 months": {
          const threeMonthsAgo = new Date(now);
          threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
          threeMonthsAgo.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= threeMonthsAgo;
          });
          break;
        }
        case "6 months": {
          const sixMonthsAgo = new Date(now);
          sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 6);
          sixMonthsAgo.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= sixMonthsAgo;
          });
          break;
        }
        case "1 year": {
          const oneYearAgo = new Date(now);
          oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
          oneYearAgo.setHours(0, 0, 0, 0);
          filtered = factorMetrics.filter(m => {
            const metricDate = new Date(m.metricDate);
            metricDate.setHours(0, 0, 0, 0);
            return metricDate >= oneYearAgo;
          });
          break;
        }
        default:
          filtered = factorMetrics;
      }

      if (filtered.length === 0) {
        metrics[filter] = null;
        return;
      }

      const sorted = [...filtered].sort((a, b) => new Date(a.metricDate).getTime() - new Date(b.metricDate).getTime());

      let cumulativeVal = 1;
      sorted.forEach(m => {
        cumulativeVal *= m.metricValue;
      });

      metrics[filter] = (cumulativeVal - 1) * 100;
    });

    return metrics;
  }, [
    availableFilters,
    entityCreateDate,
    filterV3MetricsByPeriod,
    isV3Factors,
    performanceMetrics,
    v3FixedFilters,
    v3IntrinsicAvailability,
    v3LoadedCoverage,
  ]);

  const chartData = useMemo(() => getPerformanceChartData(), [getPerformanceChartData]);
  const formattedCreateDate = useMemo(() => getFormattedCreateDate(), [getFormattedCreateDate]);
  const chartColor = seriesColor ?? (isV3Factors
    ? effectiveDarkMode ? "#E2E8F0" : "currentColor"
    : "#14b8a6");
  const defaultHeight = isV3Factors ? 560 : 420;
  const chartHeight = height ?? defaultHeight;
  const defaultMargins = isV3Factors
    ? { top: 8, right: 72, bottom: 24, left: 12 }
    : { top: 8, right: 44, bottom: 24, left: 12 };
  const chartMargins = margins ?? defaultMargins;
  const timeFilterOptions = isV3Factors ? v3TimeFilterOptions : LEGACY_TIME_FILTER_OPTIONS;

  const { svgRef, containerRef, tooltipRef } = useD3LineChart({
    visualStyle,
    data: chartData,
    lines: [
      {
        dataKey: "performance",
        stroke: chartColor,
        strokeWidth: 2,
        label: "Performance",
        showDots: false,
      },
    ],
    isDarkMode: effectiveDarkMode,
    height: chartHeight,
    margins: chartMargins,
    yAxisFormatter: (value: number) => (isV3Factors ? formatPercentage(value, 2) : `${value}%`),
    tooltipFormatter: (value: number) => formatPerformanceFn(value),
    referencePoint: formattedCreateDate
      ? { x: formattedCreateDate, color: effectiveDarkMode ? "#9CA3AF" : "#6B7280", radius: 5 }
      : undefined,
    showLegend: false,
    renderingMode: isV3Factors ? "enhanced" : "legacy",
  });

  return {
    loading,
    error,
    factors: v3Factors,
    retry,
    performanceMetrics,
    svgRef,
    containerRef,
    tooltipRef,
    chartHeight,
    chartData,
    timeFilterOptions,
    availableFilters,
    selectedPeriod,
    handleSelectPeriod,
    formattedCreateDate,
    formatPerformance: formatPerformanceFn,
    formatLocalDate: formatLocalDateFn,
    orderedFiltersForTable,
    timeframePerformance,
    v3IntrinsicAvailability,
    v3LoadedCoverage,
    v3HasDataByPeriod,
    isDarkMode,
    isV3Factors,
    entityType,
    entityName,
  };
}
